<template>
	<div class="exports">
		<HeaderLoader :is-loading="loading || (loaderEnabled && listState.isLoading.value)" />
		<Container>
			<Card>
				<template #content>
					<ExportTable
						:list-state
						@page-change="loaderEnabled = true" />
				</template>
			</Card>
		</Container>
	</div>
</template>

<script setup lang="ts">
import Card from 'primevue/card'
import Container from '@/components/Container.vue'
import HeaderLoader from '@/components/HeaderLoader.vue'
import ExportTable from '@/views/Export/components/Table.vue'
import { useExportListState } from '@/models/Export/States'
import { useRoute, useRouter } from 'vue-router'
import { onMounted, ref, watchEffect } from 'vue'
import * as Sentry from '@sentry/browser'
import StripeApi from '@/helpers/api/StripeApi'
import { useAuthStore } from '@/stores/Auth'
import { useToast } from 'primevue'

const route = useRoute()
const router = useRouter()
const stripeApi = new StripeApi()
const toast = useToast()
const authStore = useAuthStore()
const listState = useExportListState()
const loading = ref(false)
const loaderEnabled = ref(true)
listState.defaultParams = {
	with: ['build.app.repositoryConfigs', 'codeDeliveries'],
	orderBy: 'created_at',
	order: 'desc',
}
listState
	.query()
	.whereHas('build', (q) => {
		return q.where('app_id', '=', route.params.id as string)
	})
	.save()

watchEffect(() => {
	if (listState.isLoaded.value && !listState.isLoading.value) loaderEnabled.value = false
})

onMounted(() => initialize())

async function initialize() {
	if (!route.query.session_id) {
		return
	}
	loading.value = true

	try {
		const sessionId = route.query.session_id as string
		toast.add({
			severity: 'info',
			summary: 'Verifying purchase',
			detail: "We're verifying your purchase and preparing app export",
			life: 5000,
		})

		const res = await stripeApi.completeCheckout({
			session_id: sessionId,
		})

		if (res.data.status === 'open') {
			setTimeout(initialize, 2000)
			return
		} else if (res.data.status === 'complete') {
			authStore.getUser()
			toast.add({
				severity: 'success',
				summary: 'Purchase complete',
				detail: 'Your purchase was successful and your export is in progress',
				life: 5000,
			})
			await listState.getList()
			router.replace({
				query: { tab: 'exports', exportId: res.data.export!.id },
			})
		}
	} catch (error) {
		Sentry.captureException(error)
		toast.add({
			severity: 'error',
			summary: 'Could not verify purchase',
			detail: 'Please refresh this page to try again.',
			life: 5000,
		})
	} finally {
		loading.value = false
	}
}
</script>

<style lang="scss" scoped>
.exports {
	display: flex;
	flex-direction: column;
	align-items: center;
}
</style>
